import { ActionCenterTask, ActionCenterTaskType } from '@domain'
import { ActionCenterKeys } from '@services/actionCenterTasks.api'
import { ContractLoadingKeys } from '@services/contractLoading.api'
import {
	ContractLoadingSummary,
	ContractSummary,
	ContractSummaryDetail,
	ContractSummaryMatchTypeFilter,
	ContractSummaryStatus,
} from '@services/contractLoading.types'
import { QueryClient } from '@tanstack/react-query'

export type ContractCacheSnapshot = {
	previousSummaryData: ContractLoadingSummary | undefined
	previousPendingListData: ContractSummary[] | undefined
	previousTargetListData: ContractSummary[] | undefined
	updatedContractData: ContractSummaryDetail | undefined
	status: ContractSummaryStatus.ACCEPTED | ContractSummaryStatus.REJECTED
}

/**
 * Captures the current state of all relevant contract loading caches before mutation
 */
export const snapshotContractCaches = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	contractId: string | number,
	targetStatus: ContractSummaryStatus.ACCEPTED | ContractSummaryStatus.REJECTED,
	activeTab: ContractSummaryMatchTypeFilter
): Omit<ContractCacheSnapshot, 'status'> => {
	const previousSummaryData = queryClient.getQueryData<ContractLoadingSummary | undefined>(
		ContractLoadingKeys.summary(marketBasketId)
	)

	const pendingListKey = ContractLoadingKeys.list(marketBasketId, {
		status: ContractSummaryStatus.PENDING,
	})
	const previousPendingListData = queryClient.getQueryData<ContractSummary[] | undefined>(
		pendingListKey
	)

	const targetListKey = ContractLoadingKeys.list(marketBasketId, { status: targetStatus })
	const previousTargetListData = queryClient.getQueryData<ContractSummary[] | undefined>(
		targetListKey
	)

	const updatedContractData = queryClient.getQueryData<ContractSummaryDetail>(
		ContractLoadingKeys.contract(marketBasketId, contractId, { matchType: activeTab })
	)

	return {
		previousSummaryData,
		previousPendingListData,
		previousTargetListData,
		updatedContractData,
	}
}

/**
 * Updates the summary cache with optimistic changes (decrements pending, increments accepted/rejected, updates savings)
 */
export const updateSummaryCache = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	status: ContractSummaryStatus.ACCEPTED | ContractSummaryStatus.REJECTED,
	annualTotalSavings: number
): void => {
	queryClient.setQueryData<ContractLoadingSummary | undefined>(
		ContractLoadingKeys.summary(marketBasketId),
		(old) => {
			if (!old) return old

			return {
				...old,
				pending: old.pending - 1,
				accepted: status === ContractSummaryStatus.ACCEPTED ? old.accepted + 1 : old.accepted,
				rejected: status === ContractSummaryStatus.REJECTED ? old.rejected + 1 : old.rejected,
				acceptedAnnualTotalSavings:
					status === ContractSummaryStatus.ACCEPTED
						? old.acceptedAnnualTotalSavings + annualTotalSavings
						: old.acceptedAnnualTotalSavings,
			}
		}
	)
}

/**
 * Removes a contract from the pending list cache
 */
export const removeFromPendingList = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	contractId: string | number
): void => {
	const pendingListKey = ContractLoadingKeys.list(marketBasketId, {
		status: ContractSummaryStatus.PENDING,
	})

	queryClient.setQueryData<ContractSummary[] | undefined>(pendingListKey, (old) => {
		if (!old) return old
		return old.filter((contract) => contract.id !== Number(contractId))
	})
}

/**
 * Adds or updates a contract in the target list cache (accepted/rejected), sorted by annualTotalSavings descending
 */
export const addToTargetList = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	contractId: string | number,
	targetStatus: ContractSummaryStatus.ACCEPTED | ContractSummaryStatus.REJECTED,
	updatedContractData: ContractSummaryDetail | undefined
): void => {
	const targetListKey = ContractLoadingKeys.list(marketBasketId, { status: targetStatus })

	queryClient.setQueryData<ContractSummary[] | undefined>(targetListKey, (old) => {
		if (!updatedContractData?.summary) return old

		const updatedContract: ContractSummary = {
			...updatedContractData.summary,
			status: targetStatus,
		}

		const currentList = old ?? []
		const withoutCurrent = currentList.filter((contract) => contract.id !== Number(contractId))

		return [updatedContract, ...withoutCurrent].sort(
			(a, b) => b.annualTotalSavings - a.annualTotalSavings
		)
	})
}

/**
 * Updates a contract in the ALL list (unfiltered) with the new status
 */
export const updateAllList = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	contractId: string | number,
	newStatus: ContractSummaryStatus.ACCEPTED | ContractSummaryStatus.REJECTED,
	updatedContractData: ContractSummaryDetail | undefined
): void => {
	const allListKey = ContractLoadingKeys.list(marketBasketId, { status: null })

	queryClient.setQueryData<ContractSummary[] | undefined>(allListKey, (old) => {
		if (!old || !updatedContractData?.summary) return old

		return old.map((contract) =>
			contract.id === Number(contractId)
				? {
						...updatedContractData.summary,
						status: newStatus,
					}
				: contract
		)
	})
}

/**
 * Updates the detail contract cache with the new status
 */
export const updateDetailContractStatus = (
	queryClient: QueryClient,
	marketBasketId: Id,
	contractId: Id,
	newStatus: ContractSummaryStatus.ACCEPTED | ContractSummaryStatus.REJECTED,
	updatedContractData: ContractSummaryDetail | undefined,
	activeTab: ContractSummaryMatchTypeFilter
): void => {
	if (!updatedContractData) return

	queryClient.setQueryData(
		ContractLoadingKeys.contract(marketBasketId, contractId, { matchType: activeTab }),
		(old: ContractSummaryDetail | undefined) => {
			if (!old) return old

			return {
				...old,
				summary: {
					...old.summary,
					status: newStatus,
				},
			}
		}
	)
}

/**
 * Restores the contract loading caches to their previous state in case of error
 */
export const restoreCachesFromSnapshot = (
	queryClient: QueryClient,
	snapshot: ContractCacheSnapshot,
	marketBasketId: string | number
): void => {
	if (snapshot.previousSummaryData !== undefined) {
		queryClient.setQueryData(
			ContractLoadingKeys.summary(marketBasketId),
			snapshot.previousSummaryData
		)
	}

	if (snapshot.previousPendingListData !== undefined) {
		const pendingListKey = ContractLoadingKeys.list(marketBasketId, {
			status: ContractSummaryStatus.PENDING,
		})
		queryClient.setQueryData(pendingListKey, snapshot.previousPendingListData)
	}

	if (snapshot.previousTargetListData !== undefined) {
		const targetListKey = ContractLoadingKeys.list(marketBasketId, { status: snapshot.status })
		queryClient.setQueryData(targetListKey, snapshot.previousTargetListData)
	}
}

export function updateContractLoadingActionCenterCache(queryClient, marketBasketId: Id): void {
	const actionCenterKey = ActionCenterKeys.tasks(marketBasketId)

	queryClient.setQueryData(actionCenterKey, (old: ActionCenterTask[] | undefined) => {
		if (!old) return old

		return old.map((task) =>
			task.task === ActionCenterTaskType.CONTRACT_LOADING
				? {
						...task,
						completed: true,
					}
				: task
		)
	})
}
